import { getWhatsappConnectorStatus } from "@/components/connectors/api.ts";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useGateValue } from "@statsig/react-bindings";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";

/**
 * WhatsApp card for the Settings → Assistant tab, next to the "Text or email
 * Okthx" contact card. Two states keyed on `verified` — the field that means
 * "a message to the okthx number actually routes to this account" (a linked
 * but contested device is NOT verified, and a legacy code-verified user is
 * verified without ever having linked): not verified → a "Connect WhatsApp"
 * CTA into the connectors gallery; verified → a wa.me deep link that opens a
 * WhatsApp chat with the okthx number. Renders nothing when the `whatsapp`
 * gate is off or the status hasn't loaded.
 */
export const WhatsappAssistantCard = () => {
  const whatsappEnabled = useGateValue("whatsapp");

  const { data: status } = useQuery({
    queryKey: ["whatsappConnectorStatus"],
    queryFn: getWhatsappConnectorStatus,
    enabled: whatsappEnabled,
    // Changes only on explicit connect/disconnect actions, which invalidate it.
    staleTime: 60_000,
  });

  if (!whatsappEnabled || !status) return null;

  return (
    <div>
      <h2 className="text-base font-semibold mb-2">Message Okthx on WhatsApp</h2>
      <p className="text-sm text-muted-foreground mb-4">
        {status.verified
          ? "Your WhatsApp is connected — message your assistant any time."
          : "Connect your WhatsApp to message your assistant from your own number."}
      </p>
      <div className="max-w-[360px] rounded-2xl border border-border p-4">
        <div className="flex items-center gap-3">
          <WhatsAppIcon className="size-7 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">WhatsApp</p>
            <p className="truncate text-xs text-muted-foreground">
              {status.verified ? (status.okthxNumber ?? "Connected") : "Not connected"}
            </p>
          </div>
          {status.verified ? (
            // A verified user with no configured okthx number gets no button at
            // all — never the Connect CTA, which would misread as unlinked.
            status.waMeLink && (
              <Button asChild size="xs" data-statsig-name="settings-assistant-whatsapp-message">
                <a href={status.waMeLink} target="_blank" rel="noreferrer">
                  Message
                </a>
              </Button>
            )
          ) : (
            <Button
              asChild
              variant="outline"
              size="xs"
              data-statsig-name="settings-assistant-whatsapp-connect"
            >
              <Link href="/connectors">Connect WhatsApp</Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
